/**
 * useViewerPOIBatchActions — the shared "Set Systems" + "Set Locations" batch-assignment
 * state cluster (19 vars) + 6 handlers, extracted verbatim from AutodeskViewer / WebIfcViewer
 * (Phase 3.2-widened, PHASE3-VIEWER-PLAN slice 2). Config-driven: each caller injects a
 * `transport` adapter that reproduces its exact fetch-vs-apiService / body-shape / error-UX
 * drift, so per-caller behavior is preserved byte-for-byte — this hook averages nothing.
 *
 * The hook OWNS the 19 state atoms and returns them + their setters + the 6 handlers so the
 * (byte-identical) modal JSX can live in the shared <POIBatchModals> and read everything from
 * one object. `selectedPOIs` is passed IN (ADSK sources it from POITableContext, IFC from local
 * state). Callers keep any local-only code (ADSK delete-selected modal + modal-blur effect).
 *
 * Transport contract (each caller's adapter must honour it):
 *   loadSystemsProperties / loadLocationsProperties:
 *     return the loaded properties on success, or `null` to LEAVE existing state unchanged
 *     (ADSK / UnifiedPOITable non-ok + network-error → null; IFC / SitesPage error → return
 *     empty arrays so the hook resets). Adapters own their own console.error message.
 *   applySystems / applyLocations:
 *     return the result to store, `null` to no-op (UnifiedPOITable non-ok silent skip), or
 *     THROW to route into onApply*Error (ADSK alert; IFC/Sites/Unified setResult failed=count).
 */
import { useCallback, useRef, useState } from 'react';

export interface POIBatchResult {
  updated: number;
  unchanged: number;
  failed: number;
  total?: number;
}

export interface POIBatchTransport {
  /** Load available property names + values. Return null to leave state unchanged. */
  loadSystemsProperties: (
    assetId: string,
  ) => Promise<{ properties: string[]; propertyValues: Record<string, string[]> } | null>;
  /** Load available property names (locations only needs names). Return null to leave unchanged. */
  loadLocationsProperties: (assetId: string) => Promise<{ properties: string[] } | null>;
  /** Apply systems batch. Return result, null to no-op, or throw to route into onApplySystemsError. */
  applySystems: (
    assetId: string,
    poiIds: string[],
    systemProperty: string,
    subcategoryLevels: string[],
  ) => Promise<POIBatchResult | null>;
  /** Apply locations batch. Return result, null to no-op, or throw to route into onApplyLocationsError. */
  applyLocations: (
    assetId: string,
    poiIds: string[],
    storeyProperty: string,
    spaceProperty: string,
  ) => Promise<POIBatchResult | null>;
  /** Error policy for applySystems throws. Return a result to store, or void to store nothing (ADSK alert). */
  onApplySystemsError: (error: any, selectedCount: number) => POIBatchResult | void;
  /** Error policy for applyLocations throws. Return a result to store, or void to store nothing (ADSK alert). */
  onApplyLocationsError: (error: any, selectedCount: number) => POIBatchResult | void;
}

export interface UseViewerPOIBatchActionsConfig {
  /** Returns the asset id used for guards + endpoints (ADSK: assetId, IFC: asset?.id, Sites: selectedAsset?.id, Unified: siteId). */
  getAssetId: () => string | null | undefined;
  /** Selected POI ids — passed IN (ADSK from POITableContext, IFC from local state). */
  selectedPOIs: Set<string>;
  /** Reload POIs after a successful apply (ADSK/IFC/Sites take an id; Unified ignores it). */
  reloadPOIs: (assetId: string) => Promise<void> | void;
  /** Whether apply awaits the reload before clearing the applying flag (ADSK: false fire-and-forget; others: true). */
  awaitReload: boolean;
  /** Minimum subcategory-level count below which remove is a no-op (ADSK: 1 length>1 guard; IFC: 0 no guard). */
  minSubcategoryLevels: number;
  /** Transport adapter carrying this caller's exact network + error drift. */
  transport: POIBatchTransport;
}

export interface UseViewerPOIBatchActionsReturn {
  // ---- Selected POIs (re-exposed for the modal's "N selected" text) ----
  selectedPOIs: Set<string>;
  // ---- Systems state ----
  showSetSystemsModal: boolean;
  setShowSetSystemsModal: React.Dispatch<React.SetStateAction<boolean>>;
  systemsAvailableProperties: string[];
  systemsPropertyValues: Record<string, string[]>;
  selectedSystemProperty: string;
  setSelectedSystemProperty: React.Dispatch<React.SetStateAction<string>>;
  systemsSubcategoryLevels: string[];
  setSystemsSubcategoryLevels: React.Dispatch<React.SetStateAction<string[]>>;
  systemsSubcategorySearches: string[];
  setSystemsSubcategorySearches: React.Dispatch<React.SetStateAction<string[]>>;
  systemPropertySearch: string;
  setSystemPropertySearch: React.Dispatch<React.SetStateAction<string>>;
  isLoadingSystemsProperties: boolean;
  isApplyingSystems: boolean;
  systemsResult: POIBatchResult | null;
  setSystemsResult: React.Dispatch<React.SetStateAction<POIBatchResult | null>>;
  // ---- Locations state ----
  showSetLocationsModal: boolean;
  setShowSetLocationsModal: React.Dispatch<React.SetStateAction<boolean>>;
  locationsAvailableProperties: string[];
  selectedStoreyProperty: string;
  setSelectedStoreyProperty: React.Dispatch<React.SetStateAction<string>>;
  selectedSpaceProperty: string;
  setSelectedSpaceProperty: React.Dispatch<React.SetStateAction<string>>;
  storeyPropertySearch: string;
  setStoreyPropertySearch: React.Dispatch<React.SetStateAction<string>>;
  spacePropertySearch: string;
  setSpacePropertySearch: React.Dispatch<React.SetStateAction<string>>;
  isLoadingLocationsProperties: boolean;
  isApplyingLocations: boolean;
  locationsResult: POIBatchResult | null;
  setLocationsResult: React.Dispatch<React.SetStateAction<POIBatchResult | null>>;
  // ---- Handlers ----
  handleOpenSetSystemsModal: () => Promise<void>;
  handleApplySystemsBatch: () => Promise<void>;
  addSystemsSubcategoryLevel: () => void;
  removeSystemsSubcategoryLevel: (index: number) => void;
  handleOpenSetLocationsModal: () => Promise<void>;
  handleApplyLocationsBatch: () => Promise<void>;
}

export function useViewerPOIBatchActions(
  config: UseViewerPOIBatchActionsConfig,
): UseViewerPOIBatchActionsReturn {
  // Latest-ref for config: handlers read live config (selectedPOIs, transport, ids) at click
  // time without churning their identity — same footgun-2 discipline used across the viewers.
  const configRef = useRef(config);
  configRef.current = config;

  // ---- Systems state (verbatim from the viewers) ----
  const [showSetSystemsModal, setShowSetSystemsModal] = useState(false);
  const [systemsAvailableProperties, setSystemsAvailableProperties] = useState<string[]>([]);
  const [systemsPropertyValues, setSystemsPropertyValues] = useState<Record<string, string[]>>({});
  const [selectedSystemProperty, setSelectedSystemProperty] = useState('');
  const [systemsSubcategoryLevels, setSystemsSubcategoryLevels] = useState<string[]>(['']);
  const [systemsSubcategorySearches, setSystemsSubcategorySearches] = useState<string[]>(['']);
  const [systemPropertySearch, setSystemPropertySearch] = useState('');
  const [isLoadingSystemsProperties, setIsLoadingSystemsProperties] = useState(false);
  const [isApplyingSystems, setIsApplyingSystems] = useState(false);
  const [systemsResult, setSystemsResult] = useState<POIBatchResult | null>(null);

  // ---- Locations state (verbatim from the viewers) ----
  const [showSetLocationsModal, setShowSetLocationsModal] = useState(false);
  const [locationsAvailableProperties, setLocationsAvailableProperties] = useState<string[]>([]);
  const [selectedStoreyProperty, setSelectedStoreyProperty] = useState('');
  const [selectedSpaceProperty, setSelectedSpaceProperty] = useState('');
  const [storeyPropertySearch, setStoreyPropertySearch] = useState('');
  const [spacePropertySearch, setSpacePropertySearch] = useState('');
  const [isLoadingLocationsProperties, setIsLoadingLocationsProperties] = useState(false);
  const [isApplyingLocations, setIsApplyingLocations] = useState(false);
  const [locationsResult, setLocationsResult] = useState<POIBatchResult | null>(null);

  // ---- Handler: open Set Systems modal ----
  const handleOpenSetSystemsModal = useCallback(async () => {
    const cfg = configRef.current;
    const assetId = cfg.getAssetId();
    if (!assetId) return;

    setShowSetSystemsModal(true);
    setSystemsResult(null);
    setSelectedSystemProperty('');
    setSystemsSubcategoryLevels(['']);
    setSystemsSubcategorySearches(['']);
    setSystemPropertySearch('');

    setIsLoadingSystemsProperties(true);
    try {
      const data = await cfg.transport.loadSystemsProperties(assetId);
      if (data) {
        setSystemsAvailableProperties(data.properties);
        setSystemsPropertyValues(data.propertyValues);
      }
    } finally {
      setIsLoadingSystemsProperties(false);
    }
  }, []);

  // ---- Handler: apply Systems batch ----
  const handleApplySystemsBatch = useCallback(async () => {
    const cfg = configRef.current;
    const assetId = cfg.getAssetId();
    if (!assetId || !selectedSystemProperty || cfg.selectedPOIs.size === 0) return;

    setIsApplyingSystems(true);
    try {
      const validSubcategories = systemsSubcategoryLevels.filter((level) => level !== '');
      const poiIds = Array.from(cfg.selectedPOIs);

      const result = await cfg.transport.applySystems(
        assetId,
        poiIds,
        selectedSystemProperty,
        validSubcategories,
      );
      if (result) {
        setSystemsResult(result);
        if (cfg.awaitReload) {
          await cfg.reloadPOIs(assetId);
        } else {
          cfg.reloadPOIs(assetId);
        }
      }
    } catch (error) {
      const fallback = cfg.transport.onApplySystemsError(error, cfg.selectedPOIs.size);
      if (fallback) setSystemsResult(fallback);
    } finally {
      setIsApplyingSystems(false);
    }
  }, [selectedSystemProperty, systemsSubcategoryLevels]);

  // ---- Handler: add subcategory level ----
  const addSystemsSubcategoryLevel = useCallback(() => {
    setSystemsSubcategoryLevels((prev) => [...prev, '']);
    setSystemsSubcategorySearches((prev) => [...prev, '']);
  }, []);

  // ---- Handler: remove subcategory level (guard parametrized: ADSK min 1, IFC min 0) ----
  const removeSystemsSubcategoryLevel = useCallback((index: number) => {
    const minLevels = configRef.current.minSubcategoryLevels;
    setSystemsSubcategoryLevels((prev) =>
      prev.length <= minLevels ? prev : prev.filter((_, i) => i !== index),
    );
    setSystemsSubcategorySearches((prev) =>
      prev.length <= minLevels ? prev : prev.filter((_, i) => i !== index),
    );
  }, []);

  // ---- Handler: open Set Locations modal ----
  const handleOpenSetLocationsModal = useCallback(async () => {
    const cfg = configRef.current;
    const assetId = cfg.getAssetId();
    if (!assetId) return;

    setShowSetLocationsModal(true);
    setLocationsResult(null);
    setSelectedStoreyProperty('');
    setSelectedSpaceProperty('');
    setStoreyPropertySearch('');
    setSpacePropertySearch('');

    setIsLoadingLocationsProperties(true);
    try {
      const data = await cfg.transport.loadLocationsProperties(assetId);
      if (data) {
        setLocationsAvailableProperties(data.properties);
      }
    } finally {
      setIsLoadingLocationsProperties(false);
    }
  }, []);

  // ---- Handler: apply Locations batch ----
  const handleApplyLocationsBatch = useCallback(async () => {
    const cfg = configRef.current;
    const assetId = cfg.getAssetId();
    if (!assetId || (!selectedStoreyProperty && !selectedSpaceProperty) || cfg.selectedPOIs.size === 0) {
      return;
    }

    setIsApplyingLocations(true);
    try {
      const poiIds = Array.from(cfg.selectedPOIs);

      const result = await cfg.transport.applyLocations(
        assetId,
        poiIds,
        selectedStoreyProperty,
        selectedSpaceProperty,
      );
      if (result) {
        setLocationsResult(result);
        if (cfg.awaitReload) {
          await cfg.reloadPOIs(assetId);
        } else {
          cfg.reloadPOIs(assetId);
        }
      }
    } catch (error) {
      const fallback = cfg.transport.onApplyLocationsError(error, cfg.selectedPOIs.size);
      if (fallback) setLocationsResult(fallback);
    } finally {
      setIsApplyingLocations(false);
    }
  }, [selectedStoreyProperty, selectedSpaceProperty]);

  return {
    selectedPOIs: config.selectedPOIs,
    showSetSystemsModal,
    setShowSetSystemsModal,
    systemsAvailableProperties,
    systemsPropertyValues,
    selectedSystemProperty,
    setSelectedSystemProperty,
    systemsSubcategoryLevels,
    setSystemsSubcategoryLevels,
    systemsSubcategorySearches,
    setSystemsSubcategorySearches,
    systemPropertySearch,
    setSystemPropertySearch,
    isLoadingSystemsProperties,
    isApplyingSystems,
    systemsResult,
    setSystemsResult,
    showSetLocationsModal,
    setShowSetLocationsModal,
    locationsAvailableProperties,
    selectedStoreyProperty,
    setSelectedStoreyProperty,
    selectedSpaceProperty,
    setSelectedSpaceProperty,
    storeyPropertySearch,
    setStoreyPropertySearch,
    spacePropertySearch,
    setSpacePropertySearch,
    isLoadingLocationsProperties,
    isApplyingLocations,
    locationsResult,
    setLocationsResult,
    handleOpenSetSystemsModal,
    handleApplySystemsBatch,
    addSystemsSubcategoryLevel,
    removeSystemsSubcategoryLevel,
    handleOpenSetLocationsModal,
    handleApplyLocationsBatch,
  };
}
